import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { HdBodygraphCenter } from '../hd-bodygraph.models';
import { resolveHdBodygraphCenterTextColor } from '../hd-bodygraph-color-config';
import { HdBridgeGateStyle } from '../hd-chart-controls-options';
import { HdChartBasics, HdPrimaryConditioningKind } from './hd-chart-properties.types';

export interface HdBridgeGateVm {
  gate: number;
  color: string;
  textColor: string;
}

export interface HdPrimaryConditioningCenterVm {
  name: string;
  center: HdBodygraphCenter;
}

export interface HdPrimaryConditioningVm {
  label: string;
  kind: HdPrimaryConditioningKind;
  style: HdBridgeGateStyle;
  centers: HdPrimaryConditioningCenterVm[];
  gates: HdBridgeGateVm[];
  pairs: HdBridgeGateVm[][];
  genericText: string;
  longSplitInfo: string;
  /** Tooltip voor het info-icoon dat het conditionering-info-paneel opent. */
  infoTooltip: string;
}

/**
 * Primaire conditionering als weergavemodel. `t` vertaalt `humanDesign.properties.<key>`;
 * `centerColor` geeft de centerkleur van het palet waarin getoond wordt (app of print).
 */
export function buildHdPrimaryConditioningVm(
  pc: HdChartBasics['primaryConditioning'],
  t: (key: string) => string,
  centerColor: (center: HdBodygraphCenter) => string,
  style: HdBridgeGateStyle,
): HdPrimaryConditioningVm {
  const toGateVm = (g: { gate: number; center: HdBodygraphCenter }): HdBridgeGateVm => {
    const color = centerColor(g.center);
    return { gate: g.gate, color, textColor: resolveHdBodygraphCenterTextColor(color) };
  };
  return {
    label: t('primaryConditioning'),
    kind: pc.kind,
    style,
    centers: pc.centers.map(center => ({ name: t(`centerNames.${center}`), center })),
    gates: pc.gates.map(toGateVm),
    pairs: pc.pairs.map(pair => pair.map(toGateVm)),
    genericText: t('bridgeGenericText'),
    longSplitInfo: t('longSplitInfo'),
    infoTooltip: t('conditioningInfoTooltip'),
  };
}

/**
 * De waarde van de primaire conditionering (centers, brugpoorten als badge of stip, lange
 * brugparen, of de generieke tekst). Gedeeld door het Basis-paneel en de PDF-print; hover en
 * klik meldt het component enkel, de host beslist wat ermee gebeurt.
 */
@Component({
  selector: 'hd-primary-conditioning-value',
  imports: [NgTemplateOutlet],
  templateUrl: './hd-primary-conditioning-value.component.html',
  styleUrl: './hd-primary-conditioning-value.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdPrimaryConditioningValueComponent {
  readonly vm = input.required<HdPrimaryConditioningVm>();

  /** Hover op een center-naam; `null` bij verlaten. */
  readonly centerHover = output<HdBodygraphCenter | null>();
  /** Hover op een brugpoort; `null` bij verlaten. */
  readonly bridgeGateHover = output<number | null>();
  readonly bridgeGateClicked = output<{ gate: number; event: MouseEvent }>();
}
